<template>
  <div class="nl-thinking-block" v-if="isActive || keepVisible">
    <!-- Thinking header — Claude style -->
    <button class="thinking-header" @click="toggleCollapse">
      <div class="thinking-header-left">
        <svg v-if="isStreaming" class="sparkle-icon spinning" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6L8 0Z" fill="currentColor"/>
        </svg>
        <svg v-else class="sparkle-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6L8 0Z" fill="currentColor"/>
        </svg>
        <span v-if="isStreaming" class="thinking-label">Analyse en cours...</span>
        <span v-else class="thinking-label">
          Analyse terminée
          <span class="thinking-duration" v-if="elapsedTime">· {{ elapsedTime }}s</span>
        </span>
      </div>
      <div class="thinking-header-right">
        <i :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'" class="collapse-chevron"></i>
        <button @click.stop="handleClose" class="close-btn-mini">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </button>

    <!-- Thinking content -->
    <div class="thinking-body" v-show="!isCollapsed" ref="thoughtsStream">
      <div class="thinking-lines">
        <template v-for="thought in visibleThoughts" :key="thought.id">
          <!-- Simple text line for most thought types -->
          <div 
            class="thinking-line"
            :class="{ 'line-active': !thought.completed, 'line-error': thought.type === 'error' }"
          >
            <span class="line-indicator">
              <i v-if="thought.completed" class="fas fa-check"></i>
              <i v-else-if="thought.type === 'error'" class="fas fa-times"></i>
              <span v-else class="dot-pulse"></span>
            </span>
            <span class="line-text">{{ thought.message }}</span>
          </div>

          <!-- Inline expandable: Analysis details -->
          <div v-if="thought.type === 'analyzing' && thought.data" class="inline-details">
            <span v-if="thought.data.core_role" class="detail-chip">
              {{ thought.data.core_role }}
            </span>
            <span v-if="thought.data.primary_signal_label" class="detail-chip accent">
              {{ thought.data.primary_signal_label }}
            </span>
            <span v-if="thought.data.seniority" class="detail-chip">
              {{ thought.data.seniority }}
            </span>
          </div>

          <!-- Inline expandable: Variants -->
          <div v-if="thought.type === 'variants' && thought.data" class="inline-expand">
            <button @click="toggleSection('v-' + thought.id)" class="inline-expand-btn">
              <span>{{ thought.data.titles?.length || 0 }} titres</span>
              <i :class="expandedSections['v-' + thought.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
            <div v-show="expandedSections['v-' + thought.id]" class="inline-expand-body">
              <div class="chip-list">
                <span v-for="(title, idx) in thought.data.titles" :key="'t-'+idx" class="mini-chip">{{ title }}</span>
              </div>
              <div v-if="thought.data.titles_with_skills?.length" class="chip-list" style="margin-top: 0.375rem;">
                <span class="chip-list-label">+ compétences :</span>
                <span v-for="(title, idx) in thought.data.titles_with_skills" :key="'ts-'+idx" class="mini-chip accent">{{ title }}</span>
              </div>
            </div>
          </div>

          <!-- Inline expandable: Skills -->
          <div v-if="thought.type === 'skills' && thought.data" class="inline-expand">
            <button @click="toggleSection('s-' + thought.id)" class="inline-expand-btn">
              <span>Compétences identifiées</span>
              <i :class="expandedSections['s-' + thought.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
            <div v-show="expandedSections['s-' + thought.id]" class="inline-expand-body">
              <div v-if="thought.data.explicit?.length" class="skill-row">
                <span class="skill-row-label">Requises</span>
                <span v-for="skill in thought.data.explicit" :key="skill" class="mini-chip explicit">{{ skill }}</span>
              </div>
              <div v-if="thought.data.implied?.length" class="skill-row">
                <span class="skill-row-label">Implicites</span>
                <span v-for="skill in thought.data.implied" :key="skill" class="mini-chip implied">{{ skill }}</span>
              </div>
              <div v-if="thought.data.related?.length" class="skill-row">
                <span class="skill-row-label">Connexes</span>
                <span v-for="skill in thought.data.related" :key="skill" class="mini-chip related">{{ skill }}</span>
              </div>
            </div>
          </div>

          <!-- Inline: Strategy reasoning -->
          <div v-if="thought.type === 'strategy' && thought.data?.reasoning" class="inline-details">
            <span class="reasoning-inline">{{ thought.data.reasoning }}</span>
            <span v-if="thought.data.use_semantic" class="semantic-pill">
              <i class="fas fa-brain"></i> sémantique
            </span>
          </div>

          <!-- Inline: ES results stats -->
          <div v-if="thought.type === 'es_results' && thought.data" class="inline-details">
            <span class="stat-pill">{{ thought.data.total_found }} trouvés</span>
            <span class="stat-pill">{{ thought.data.analyzing_count }} à analyser</span>
          </div>

          <!-- Inline: Rerank complete -->
          <div v-if="thought.type === 'rerank_complete' && thought.data" class="inline-details">
            <span class="stat-pill accent">{{ thought.data.selected_count }} pertinents</span>
            <span v-if="thought.data.reasoning" class="reasoning-inline">{{ thought.data.reasoning }}</span>
          </div>
        </template>
      </div>

      <!-- Pulsing indicator while streaming -->
      <div v-if="isStreaming" class="thinking-pulse">
        <span></span><span></span><span></span>
      </div>
    </div>

    <!-- Results summary (always visible, outside collapsible area) -->
    <div v-if="results && !isStreaming" class="results-bar">
      <div class="results-bar-stats">
        <span class="results-count">{{ results.ranked_count || results.results?.length || 0 }}</span>
        <span class="results-label">candidats classés</span>
        <span class="results-divider">·</span>
        <span class="results-total">{{ results.total_hits }} total</span>
      </div>
      <button @click="viewResults" class="results-btn">
        Voir les résultats
        <i class="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, reactive } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import authService from '@/services/auth';

export default {
  name: 'NLSearchStream',
  props: {
    query: {
      type: String,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'view-results', 'error'],
  setup(props, { emit }) {
    const router = useRouter();
    const thoughts = ref([]);
    const isStreaming = ref(false);
    const results = ref(null);
    const thoughtsStream = ref(null);
    const keepVisible = ref(false);
    const isCollapsed = ref(false);
    const expandedSections = reactive({});
    const currentStep = ref(0);
    const totalSteps = ref(6);
    const startTime = ref(null);
    const elapsedTime = ref(null);
    
    let eventSource = null;
    let thoughtIdCounter = 0;
    let timerInterval = null;

    const progressPercent = ref(0);

    // Filter out debug thoughts from display
    const visibleThoughts = computed(() => {
      return thoughts.value.filter(t => t.type !== 'debug');
    });

    const getApiBaseUrl = () => {
      if (process.env.NODE_ENV === 'development') {
        return process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000';
      }
      return '';
    };

    const toggleSection = (sectionId) => {
      expandedSections[sectionId] = !expandedSections[sectionId];
    };

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const handleClose = () => {
      keepVisible.value = false;
      emit('close');
    };

    // Timer management
    const startTimer = () => {
      startTime.value = Date.now();
      elapsedTime.value = null;
      timerInterval = setInterval(() => {
        if (startTime.value) {
          elapsedTime.value = ((Date.now() - startTime.value) / 1000).toFixed(1);
        }
      }, 100);
    };

    const stopTimer = () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
      if (startTime.value) {
        elapsedTime.value = ((Date.now() - startTime.value) / 1000).toFixed(1);
      }
    };

    const addThought = (type, message, data = null, completed = false) => {
      if (data?.step) {
        currentStep.value = data.step;
        if (data.total_steps) totalSteps.value = data.total_steps;
        progressPercent.value = (data.step / totalSteps.value) * 100;
      }

      // Mark previous thought as completed
      if (thoughts.value.length > 0) {
        thoughts.value[thoughts.value.length - 1].completed = true;
      }

      thoughts.value.push({
        id: thoughtIdCounter++,
        type,
        message,
        data,
        completed,
        timestamp: Date.now()
      });

      nextTick(() => {
        if (thoughtsStream.value) {
          thoughtsStream.value.scrollTop = thoughtsStream.value.scrollHeight;
        }
      });
    };

    const startStreaming = async () => {
      if (!props.query) return;

      thoughts.value = [];
      results.value = null;
      isStreaming.value = true;
      keepVisible.value = true;
      isCollapsed.value = false;
      currentStep.value = 0;
      progressPercent.value = 0;
      startTimer();

      const abortController = new AbortController();
      eventSource = abortController;

      try {
        const token = await authService.getCurrentToken();
        if (!token) throw new Error('Token d\'authentification manquant');

        const baseUrl = getApiBaseUrl();
        const url = `${baseUrl}/api/v1/nl_search/stream?query=${encodeURIComponent(props.query)}&limit=20`;

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          signal: abortController.signal
        });

        if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            isStreaming.value = false;
            stopTimer();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';
          
          for (const message of messages) {
            if (message.startsWith('data: ')) {
              try {
                const jsonStr = message.substring(6);
                const data = JSON.parse(jsonStr);
                
                switch (data.type) {
                  case 'thinking':
                    addThought('thinking', data.data.message, data.data);
                    break;
                  case 'analyzing':
                    addThought('analyzing', data.data.message, data.data);
                    break;
                  case 'variants':
                    addThought('variants', data.data.message, data.data);
                    break;
                  case 'skills':
                    addThought('skills', data.data.message, data.data);
                    break;
                  case 'strategy':
                    addThought('strategy', data.data.message, data.data);
                    break;
                  case 'searching':
                    addThought('searching', data.data.message, data.data);
                    break;
                  case 'es_results':
                    addThought('es_results', data.data.message, data.data);
                    break;
                  case 'reranking':
                    addThought('reranking', data.data.message, data.data);
                    break;
                  case 'rerank_complete':
                    addThought('rerank_complete', data.data.message, data.data);
                    break;
                  // Debug events: stored in thoughts array but filtered from display via visibleThoughts
                  case 'debug':
                    addThought('debug', data.data.label || 'Debug', data.data);
                    break;
                  case 'complete':
                    addThought('complete', data.data.message, data.data, true);
                    results.value = data.data;
                    isStreaming.value = false;
                    stopTimer();
                    progressPercent.value = 100;
                    // Auto-collapse thinking content after short delay
                    setTimeout(() => { isCollapsed.value = true; }, 800);
                    break;
                  case 'error':
                    addThought('error', data.data.message, null, true);
                    isStreaming.value = false;
                    stopTimer();
                    emit('error', data.data.message);
                    break;
                }
              } catch (err) {
                console.error('Erreur parsing SSE:', err);
              }
            }
          }
        }

      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Stream annulé');
          return;
        }
        
        console.error('Erreur streaming:', error);
        addThought('error', `Échec de la recherche: ${error.message}`, null, true);
        isStreaming.value = false;
        stopTimer();
        emit('error', error.message);
      }
    };

    const stopStreaming = () => {
      if (eventSource && eventSource.abort) {
        eventSource.abort();
        eventSource = null;
      }
      isStreaming.value = false;
      stopTimer();
    };

    watch(
      () => ({ query: props.query, active: props.isActive }),
      (newVal, oldVal) => {
        const shouldStart = newVal.active && newVal.query;
        const wasActive = oldVal?.active && oldVal?.query;
        
        if (shouldStart && (!wasActive || newVal.query !== oldVal?.query)) {
          stopStreaming();
          startStreaming();
        } else if (!newVal.active && wasActive) {
          stopStreaming();
        }
      },
      { immediate: false }
    );

    const viewResults = async () => {
      if (!results.value?.results?.length) {
        alert("Aucun résultat à afficher.");
        return;
      }

      try {
        const candidateIds = results.value.results.map(r => r.metadata?.candidate_id || r.document_id);
        const scoresMap = results.value.scores || {};
        
        if (Object.keys(scoresMap).length === 0) {
          results.value.results.forEach((r, i) => {
            const id = r.candidate_id || r.metadata?.candidate_id || r.document_id;
            scoresMap[id] = (results.value.results.length - i) * 100;
          });
        }

        const setResponse = await axios.post('/api/v1/search-sets/', {
          candidate_ids: candidateIds,
          metadata: {
            source: 'nl_search',
            original_query: props.query,
            total_hits: results.value.total_hits,
            analysis: results.value.analysis || {},
            rerank_reasoning: results.value.rerank_reasoning || '',
            scores: scoresMap,
            job_titles: results.value.job_titles || {}
          }
        });

        isCollapsed.value = true;
        
        await new Promise(resolve => setTimeout(resolve, 50));

        router.push({
          path: '/',
          query: {
            search_set: setResponse.data.search_set_id,
            auto_search: 'true',
            from: 'nl-search'
          }
        });

      } catch (err) {
        console.error('Échec création search_set:', err);
        alert('Impossible d\'ouvrir les résultats. Veuillez réessayer.');
      }
    };

    return {
      thoughts,
      visibleThoughts,
      isStreaming,
      results,
      thoughtsStream,
      keepVisible,
      isCollapsed,
      expandedSections,
      currentStep,
      totalSteps,
      progressPercent,
      elapsedTime,
      toggleSection,
      toggleCollapse,
      handleClose,
      viewResults
    };
  }
};
</script>

<style scoped>
/* ─── Claude-style thinking block ─── */
.nl-thinking-block {
  background: var(--thinking-bg, #faf8f5);
  border: 1px solid var(--thinking-border, #e8e0d4);
  border-radius: 12px;
  margin: 1rem 0;
  overflow: hidden;
  color: var(--text-color);
}

/* ─── Header ─── */
.thinking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 1rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--thinking-border, #e8e0d4);
  cursor: pointer;
  width: 100%;
  transition: background 0.15s;
}

.thinking-header:hover {
  background: rgba(0, 0, 0, 0.02);
}

.thinking-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sparkle-icon {
  color: var(--primary-color, #c77b2f);
  flex-shrink: 0;
}

.sparkle-icon.spinning {
  animation: sparkle-spin 2s linear infinite;
}

@keyframes sparkle-spin {
  0% { transform: rotate(0deg); opacity: 1; }
  50% { transform: rotate(180deg); opacity: 0.6; }
  100% { transform: rotate(360deg); opacity: 1; }
}

.thinking-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary, #8b7355);
}

.thinking-duration {
  font-weight: 400;
  color: var(--text-tertiary, #a89880);
}

.thinking-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.collapse-chevron {
  font-size: 0.65rem;
  color: var(--text-tertiary, #a89880);
  transition: transform 0.2s;
}

.close-btn-mini {
  background: transparent;
  border: none;
  color: var(--text-tertiary, #a89880);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  font-size: 0.7rem;
  line-height: 1;
}

.close-btn-mini:hover {
  color: var(--text-color);
  background: rgba(0, 0, 0, 0.06);
}

/* ─── Body ─── */
.thinking-body {
  max-height: 350px;
  overflow-y: auto;
  padding: 0.75rem 1rem;
}

.thinking-body::-webkit-scrollbar {
  width: 3px;
}

.thinking-body::-webkit-scrollbar-thumb {
  background: var(--thinking-border, #e8e0d4);
  border-radius: 2px;
}

/* ─── Thought lines ─── */
.thinking-lines {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.thinking-line {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.25rem 0;
  animation: line-in 0.25s ease;
  font-size: 0.8rem;
  color: var(--text-secondary, #6b6050);
  line-height: 1.45;
}

.thinking-line.line-active {
  color: var(--text-color, #3d3529);
}

.thinking-line.line-error {
  color: #c0392b;
}

@keyframes line-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.line-indicator {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.line-indicator .fa-check {
  font-size: 8px;
  color: var(--primary-color, #c77b2f);
  opacity: 0.6;
}

.line-indicator .fa-times {
  font-size: 8px;
  color: #c0392b;
}

.line-text {
  flex: 1;
}

/* Dot pulse for active line */
.dot-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color, #c77b2f);
  animation: dot-blink 1s ease-in-out infinite;
}

@keyframes dot-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}

/* ─── Inline details (chips row) ─── */
.inline-details {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0 0.25rem 1.375rem;
  animation: line-in 0.25s ease;
}

.detail-chip {
  padding: 0.15rem 0.5rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  font-size: 0.72rem;
  color: var(--text-secondary, #6b6050);
  font-weight: 500;
}

.detail-chip.accent {
  background: rgba(199, 123, 47, 0.1);
  color: var(--primary-color, #c77b2f);
}

.stat-pill {
  padding: 0.15rem 0.5rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary, #6b6050);
}

.stat-pill.accent {
  background: rgba(199, 123, 47, 0.1);
  color: var(--primary-color, #c77b2f);
}

.reasoning-inline {
  font-size: 0.72rem;
  color: var(--text-tertiary, #8b7b65);
  font-style: italic;
  line-height: 1.4;
}

.semantic-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  background: rgba(156, 39, 176, 0.08);
  color: #7b1fa2;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
}

/* ─── Inline expandable sections ─── */
.inline-expand {
  padding: 0.125rem 0 0.25rem 1.375rem;
  animation: line-in 0.25s ease;
}

.inline-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.2rem 0.5rem;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.72rem;
  color: var(--text-secondary, #6b6050);
  transition: all 0.15s;
}

.inline-expand-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}

.inline-expand-btn i {
  font-size: 0.55rem;
  color: var(--text-tertiary, #a89880);
}

.inline-expand-body {
  margin-top: 0.375rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

/* Chip lists (variants, skills) */
.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.chip-list-label {
  font-size: 0.65rem;
  color: var(--text-tertiary, #a89880);
  margin-right: 0.25rem;
}

.mini-chip {
  padding: 0.125rem 0.375rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 3px;
  font-size: 0.68rem;
  color: var(--text-secondary, #6b6050);
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.mini-chip.accent {
  background: rgba(199, 123, 47, 0.08);
  color: var(--primary-color, #c77b2f);
}

/* Skill rows */
.skill-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.375rem;
}

.skill-row:last-child {
  margin-bottom: 0;
}

.skill-row-label {
  font-size: 0.65rem;
  color: var(--text-tertiary, #a89880);
  min-width: 60px;
  font-weight: 500;
}

.mini-chip.explicit {
  background: rgba(40, 167, 69, 0.08);
  color: #228b22;
}

.mini-chip.implied {
  background: rgba(0, 123, 255, 0.08);
  color: #0066cc;
}

.mini-chip.related {
  background: rgba(108, 117, 125, 0.06);
  color: #6c757d;
}

/* ─── Thinking pulse (bottom indicator) ─── */
.thinking-pulse {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0 0.25rem;
}

.thinking-pulse span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--primary-color, #c77b2f);
  animation: pulse-bounce 1.2s infinite ease-in-out;
}

.thinking-pulse span:nth-child(1) { animation-delay: -0.32s; }
.thinking-pulse span:nth-child(2) { animation-delay: -0.16s; }

@keyframes pulse-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40% { transform: scale(1); opacity: 0.8; }
}

/* ─── Results bar ─── */
.results-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--thinking-border, #e8e0d4);
  background: rgba(199, 123, 47, 0.03);
}

.results-bar-stats {
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: var(--text-secondary, #6b6050);
}

.results-count {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-color, #c77b2f);
}

.results-label {
  font-weight: 500;
}

.results-divider {
  color: var(--text-tertiary, #a89880);
}

.results-total {
  color: var(--text-tertiary, #a89880);
}

.results-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: var(--primary-color, #c77b2f);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.results-btn:hover {
  filter: brightness(1.1);
  transform: translateX(2px);
}

.results-btn i {
  font-size: 0.65rem;
}

/* ─── Dark mode ─── */
:root[data-theme="dark"] .nl-thinking-block {
  background: var(--bg-secondary, #1c1a17);
  border-color: var(--border-color, #3d3529);
}

:root[data-theme="dark"] .thinking-header {
  border-bottom-color: var(--border-color, #3d3529);
}

:root[data-theme="dark"] .thinking-header:hover {
  background: rgba(255, 255, 255, 0.02);
}

:root[data-theme="dark"] .thinking-line {
  color: var(--text-secondary, #a89880);
}

:root[data-theme="dark"] .thinking-line.line-active {
  color: var(--text-color, #e0d5c5);
}

:root[data-theme="dark"] .detail-chip {
  background: rgba(255, 255, 255, 0.06);
}

:root[data-theme="dark"] .mini-chip {
  background: rgba(255, 255, 255, 0.06);
}

:root[data-theme="dark"] .inline-expand-btn {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

:root[data-theme="dark"] .inline-expand-body {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.05);
}

:root[data-theme="dark"] .results-bar {
  border-top-color: var(--border-color, #3d3529);
  background: rgba(199, 123, 47, 0.05);
}

:root[data-theme="dark"] .stat-pill {
  background: rgba(255, 255, 255, 0.06);
}
</style>